import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { format, addDays, startOfDay, isBefore, isToday } from 'date-fns';
import { Zap, CheckCircle, ArrowRight, ArrowLeft, Calendar as CalendarIcon, Clock, User, Phone, Mail, MapPin, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../lib/api';
import { toast } from 'sonner';

const BookEstimatePage = () => {
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_type: 'residential',
    address: '',
    location_area: 'victoria',
    notes: ''
  });

  // Sort services: Residential, Commercial first
  const serviceOrder = ['residential-electrical', 'commercial-electrical', 'ev-charger-installation', 'panel-replacement', 'generator-installation', 'lighting-installation'];
  const sortedServices = [...services].sort((a, b) => {
    const aIndex = serviceOrder.indexOf(a.slug);
    const bIndex = serviceOrder.indexOf(b.slug);
    return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
  });

  useEffect(() => {
    const loadServices = async () => {
      try {
        const response = await api.getServices();
        setServices(response.data);
      } catch (error) {
        console.error('Error loading services:', error);
      }
    };
    loadServices();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      loadAvailability(format(selectedDate, 'yyyy-MM-dd'));
    }
  }, [selectedDate]);

  const loadAvailability = async (date) => {
    try {
      const response = await api.getAvailability(date);
      setAvailableSlots(response.data.slots || []);
      setSelectedTime('');
    } catch (error) {
      console.error('Error loading availability:', error);
      setAvailableSlots([]);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const bookingData = {
        ...formData,
        service_type: selectedService,
        preferred_date: format(selectedDate, 'yyyy-MM-dd'),
        preferred_time: selectedTime
      };
      
      const response = await api.createBooking(bookingData);
      setBookingDetails(response.data.booking);
      setBookingComplete(true);
      toast.success('Booking confirmed!');
    } catch (error) {
      toast.error('Failed to create booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isDateDisabled = (date) => {
    const today = startOfDay(new Date());
    return isBefore(date, today) || date.getDay() === 0; // Disable past dates and Sundays
  };

  const steps = [
    { number: 1, title: 'Select Service' },
    { number: 2, title: 'Choose Date & Time' },
    { number: 3, title: 'Your Details' },
    { number: 4, title: 'Confirm' }
  ];

  if (bookingComplete) {
    return (
      <div className="min-h-screen bg-primary-dark pt-20" data-testid="booking-success">
        <section className="py-24">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="w-20 h-20 bg-electric-teal rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-primary-dark" />
              </div>
              <h1 className="text-4xl font-bold text-white mb-4">Booking Confirmed!</h1>
              <p className="text-white/70 text-lg mb-8">
                Thank you for booking with Island Coastal Electric. We'll see you soon!
              </p>
              
              <div className="bg-secondary-dark p-8 rounded-lg border border-white/10 text-left">
                <h3 className="text-white font-bold text-xl mb-6">Booking Details</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Zap className="w-5 h-5 text-electric-teal" />
                    <span className="text-white/70">Service: </span>
                    <span className="text-white font-medium">{selectedService}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CalendarIcon className="w-5 h-5 text-electric-teal" />
                    <span className="text-white/70">Date: </span>
                    <span className="text-white font-medium">
                      {selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy')}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-electric-teal" />
                    <span className="text-white/70">Time: </span>
                    <span className="text-white font-medium">
                      {availableSlots.find(s => s.time === selectedTime)?.display || selectedTime}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-electric-teal" />
                    <span className="text-white/70">Address: </span>
                    <span className="text-white font-medium">{formData.address}</span>
                  </div>
                </div>
              </div>
              
              <p className="text-white/60 text-sm mt-6">
                A confirmation email will be sent to {formData.customer_email}. You'll also receive a reminder the day before your appointment.
              </p>
              
              <Button 
                onClick={() => window.location.href = '/'}
                className="mt-8 bg-electric-teal text-primary-dark font-bold uppercase tracking-wider px-8 py-4 hover:bg-white transition-all"
                data-testid="back-home-btn"
              >
                Back to Home
              </Button>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-dark pt-28 lg:pt-32" data-testid="book-estimate-page">
      {/* Header */}
      <section className="py-12 bg-secondary-dark">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="text-electric-teal font-bold uppercase tracking-widest text-sm">
              Free Estimate
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight mt-4 mb-6">
              Book Your Appointment
            </h1>
          </motion.div>
          
          {/* Step Indicator */}
          <div className="flex justify-between items-center mt-12 max-w-2xl mx-auto">
            {steps.map((s, index) => (
              <React.Fragment key={s.number}>
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
                    step >= s.number 
                      ? 'bg-electric-teal text-primary-dark' 
                      : 'bg-white/10 text-white/50'
                  }`}>
                    {step > s.number ? <CheckCircle className="w-5 h-5" /> : s.number}
                  </div>
                  <span className={`text-xs font-medium mt-2 hidden sm:block ${
                    step >= s.number ? 'text-electric-teal' : 'text-white/50'
                  }`}>
                    {s.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${
                    step > s.number ? 'bg-electric-teal' : 'bg-white/10'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12 bg-primary-dark">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-secondary-dark p-8 rounded-lg border border-white/10">
            {/* Step 1: Service Selection */}
            {step === 1 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                data-testid="step-1"
              >
                <h2 className="text-2xl font-bold text-white mb-2">Select a Service</h2>
                <p className="text-white/40 text-sm mb-6">Tap a service to continue</p>
                <div className="space-y-3">
                  {sortedServices.map((service) => (
                    <button
                      key={service.id}
                      onClick={() => {
                        setSelectedService(service.title);
                        setTimeout(() => setStep(2), 300);
                      }}
                      className={`w-full text-left p-4 rounded-lg border transition-all ${
                        selectedService === service.title
                          ? 'bg-electric-teal/10 border-electric-teal text-white'
                          : 'bg-white/5 border-white/10 text-white/70 hover:border-white/30'
                      }`}
                      data-testid={`service-option-${service.slug}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-semibold">{service.title}</span>
                          <p className="text-sm mt-1 opacity-70">{service.short_description}</p>
                        </div>
                        <ArrowRight className="w-5 h-5 text-electric-teal opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                      </div>
                    </button>
                  ))}
                </div>
                
                <div className="mt-8 flex justify-end">
                  <Button
                    onClick={() => setStep(2)}
                    disabled={!selectedService}
                    className="bg-electric-teal text-primary-dark font-bold uppercase tracking-wider px-8 hover:bg-white transition-all disabled:opacity-50"
                    data-testid="next-step-1"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 2: Date & Time Selection */}
            {step === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                data-testid="step-2"
              >
                <h2 className="text-2xl font-bold text-white mb-6">Choose Date & Time</h2>
                
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <Label className="text-white mb-3 block">Select Date</Label>
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={isDateDisabled}
                      className="rounded-md border border-white/10 bg-primary-dark p-3"
                      data-testid="booking-calendar"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-white mb-3 block">Available Times</Label>
                    {selectedDate ? (
                      availableSlots.length > 0 ? (
                        <div className="grid grid-cols-2 gap-2">
                          {availableSlots.map((slot) => (
                            <button
                              key={slot.time}
                              onClick={() => setSelectedTime(slot.time)}
                              className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                                selectedTime === slot.time
                                  ? 'bg-electric-teal text-primary-dark border-electric-teal'
                                  : 'bg-white/5 border-white/10 text-white hover:border-white/30'
                              }`}
                              data-testid={`time-slot-${slot.time}`}
                            >
                              {slot.display}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="text-white/60 text-sm">No available slots for this date.</p>
                      )
                    ) : (
                      <p className="text-white/60 text-sm">Please select a date first.</p>
                    )}
                  </div>
                </div>
                
                <div className="mt-8 flex justify-between">
                  <Button
                    onClick={() => setStep(1)}
                    variant="outline"
                    className="border-white/30 text-white font-bold uppercase tracking-wider px-8 hover:bg-white/10"
                    data-testid="prev-step-2"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={() => setStep(3)}
                    disabled={!selectedDate || !selectedTime}
                    className="bg-electric-teal text-primary-dark font-bold uppercase tracking-wider px-8 hover:bg-white transition-all disabled:opacity-50"
                    data-testid="next-step-2"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Contact Details */}
            {step === 3 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                data-testid="step-3"
              >
                <h2 className="text-2xl font-bold text-white mb-6">Your Details</h2>
                
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-white mb-2 block">Full Name</Label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.customer_name}
                      onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                      className="bg-primary-dark border-white/20 text-white focus:border-electric-teal"
                      placeholder="John Smith"
                      required
                      data-testid="booking-name"
                    />
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="email" className="text-white mb-2 block">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.customer_email}
                        onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                        className="bg-primary-dark border-white/20 text-white focus:border-electric-teal"
                        placeholder="john@example.com"
                        required
                        data-testid="booking-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-white mb-2 block">Phone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.customer_phone}
                        onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                        className="bg-primary-dark border-white/20 text-white focus:border-electric-teal"
                        placeholder="778-555-0123"
                        required
                        data-testid="booking-phone"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-white mb-2 block">Customer Type</Label>
                      <Select 
                        value={formData.customer_type} 
                        onValueChange={(value) => setFormData({ ...formData, customer_type: value })}
                      >
                        <SelectTrigger className="bg-primary-dark border-white/20 text-white" data-testid="booking-customer-type">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="residential">Residential</SelectItem>
                          <SelectItem value="commercial">Commercial</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-white mb-2 block">Location Area</Label>
                      <Select 
                        value={formData.location_area} 
                        onValueChange={(value) => setFormData({ ...formData, location_area: value })}
                      >
                        <SelectTrigger className="bg-primary-dark border-white/20 text-white" data-testid="booking-location-area">
                          <SelectValue placeholder="Select area" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="victoria">Victoria / Southern Area</SelectItem>
                          <SelectItem value="duncan_north">North of Duncan</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="address" className="text-white mb-2 block">Service Address</Label>
                    <Input
                      id="address"
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="bg-primary-dark border-white/20 text-white focus:border-electric-teal"
                      placeholder="123 Main St, Victoria, BC"
                      required
                      data-testid="booking-address"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="notes" className="text-white mb-2 block">Details about expected work and any additional information</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="bg-primary-dark border-white/20 text-white focus:border-electric-teal min-h-[100px]"
                      placeholder="Please describe the work you need done, any specific requirements, access instructions, etc."
                      data-testid="booking-notes"
                    />
                  </div>
                </div>
                
                <div className="mt-8 flex justify-between">
                  <Button
                    onClick={() => setStep(2)}
                    variant="outline"
                    className="border-white/30 text-white font-bold uppercase tracking-wider px-8 hover:bg-white/10"
                    data-testid="prev-step-3"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={() => setStep(4)}
                    disabled={!formData.customer_name || !formData.customer_email || !formData.customer_phone || !formData.address}
                    className="bg-electric-teal text-primary-dark font-bold uppercase tracking-wider px-8 hover:bg-white transition-all disabled:opacity-50"
                    data-testid="next-step-3"
                  >
                    Review Booking
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 4: Confirmation */}
            {step === 4 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                data-testid="step-4"
              >
                <h2 className="text-2xl font-bold text-white mb-6">Review & Confirm</h2>
                
                <div className="space-y-6">
                  <div className="bg-primary-dark p-6 rounded-lg">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Zap className="w-5 h-5 text-electric-teal" />
                      Service
                    </h3>
                    <p className="text-white/80">{selectedService}</p>
                  </div>
                  
                  <div className="bg-primary-dark p-6 rounded-lg">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <CalendarIcon className="w-5 h-5 text-electric-teal" />
                      Date & Time
                    </h3>
                    <p className="text-white/80">
                      {selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy')}
                    </p>
                    <p className="text-white/60">
                      {availableSlots.find(s => s.time === selectedTime)?.display || selectedTime}
                    </p>
                  </div>
                  
                  <div className="bg-primary-dark p-6 rounded-lg">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <User className="w-5 h-5 text-electric-teal" />
                      Contact Information
                    </h3>
                    <div className="space-y-2 text-white/80">
                      <p>{formData.customer_name}</p>
                      <p>{formData.customer_email}</p>
                      <p>{formData.customer_phone}</p>
                      <p>{formData.address}</p>
                    </div>
                  </div>
                  
                  {formData.notes && (
                    <div className="bg-primary-dark p-6 rounded-lg">
                      <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-electric-teal" />
                        Additional Notes
                      </h3>
                      <p className="text-white/80">{formData.notes}</p>
                    </div>
                  )}
                </div>
                
                <div className="mt-8 flex justify-between">
                  <Button
                    onClick={() => setStep(3)}
                    variant="outline"
                    className="border-white/30 text-white font-bold uppercase tracking-wider px-8 hover:bg-white/10"
                    data-testid="prev-step-4"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="bg-electric-teal text-primary-dark font-bold uppercase tracking-wider px-8 hover:bg-white transition-all btn-glow"
                    data-testid="confirm-booking"
                  >
                    {loading ? 'Booking...' : 'Confirm Booking'}
                    <CheckCircle className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>
      
      {/* What to Expect */}
      {!bookingComplete && (
        <section className="py-16 bg-secondary-dark" data-testid="what-to-expect">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="text-2xl font-bold text-white mb-8 text-center">What to Expect</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { step: '1', title: 'Book Online', desc: 'Pick a service, date, and time that works for you.' },
                { step: '2', title: 'Estimator Visit', desc: 'A certified electrician visits your property to assess the work.' },
                { step: '3', title: 'Get Your Quote', desc: 'Receive a detailed, transparent estimate within 24 hours.' },
                { step: '4', title: 'Work Begins', desc: 'Approve the quote and we schedule your project start date.' }
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className="w-10 h-10 rounded-full bg-electric-teal text-primary-dark font-bold flex items-center justify-center mx-auto mb-3 text-sm">
                    {item.step}
                  </div>
                  <h4 className="text-white font-semibold mb-1 text-sm">{item.title}</h4>
                  <p className="text-white/50 text-xs leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BookEstimatePage;
